import React, { useEffect, useState } from "react";
import { Button, Col, Input, Row, Select } from "antd";
import AppInput from "../../../../components/AppInput";
import moment from "moment";
import VoyageDetails from "../../../../components/VoyageDetails";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";
import { useNavigate } from "react-router-dom";
import { fuelTypeOptions } from "../../../../Data/data";
import useUserData from "../../../../hooks/useUserData";

const { Option } = Select;

function AddBunkerDetails() {
  const user = useUserData();
  const designation = user?.data?.designation;
  const navigate = useNavigate();

  const [loader, setLoader] = useState(false);
  const [staffDashboard, setStaffDashboard] = useState("");
  const [subTankList, setSubTankList] = useState([]);
  const [errors, setErrors] = useState({});

  // ✅ single form state including tanks
  const [form, setForm] = useState({
    bunkerDate: moment().format("YYYY-MM-DD"),
    bdnReferenceNo: "",
    bunkeringLocation: "",
    supplierName: "",
    chartererOrOwner: "",
    bargeNameAndImo: "",
    grade: "",
    density: "",
    sulphur: "",
    chiefEngSign: "",
    totalquantity: "",
    tanks: [{ tankId: null, quantity: "" }],
  });

  // Change handlers
  const handleChange = (key, value) => {
    // Prevent negative numbers for quantity and specific metrics
    if (["totalquantity", "sulphur", "density"].includes(key) && value < 0) {
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" })); // Clear error when user types
  };

  const handleTankChange = (index, value) => {
    const updated = [...form.tanks];
    updated[index].tankId = value;
    setForm((prev) => ({ ...prev, tanks: updated }));
  };

  const handleQuantityChange = (index, value) => {
    if (value < 0) return; // Prevent negative numbers
    const updated = [...form.tanks];
    updated[index].quantity = value;
    setForm((prev) => ({ ...prev, tanks: updated }));
  };

  const handleAddTank = () => {
    setForm((prev) => ({
      ...prev,
      tanks: [...prev.tanks, { tankId: null, quantity: "" }],
    }));
  };

  // Fetch sub tanks
  const LIST_SUB_TANKS_API = async () => {
    const FORM_DATA = new FormData();
    const staffDetails = JSON.parse(localStorage.getItem("user_data"));
    FORM_DATA.append("ship_id", staffDetails?.data?.ship_details?.ship_id);

    try {
      const res = await post(ApiURL?.bunker_report_list, FORM_DATA);
      if (res?.status) {
        setSubTankList(res?.tanks || []);
      }
    } catch (error) {
      console.error("Error fetching Sub Tanks list:", error);
    }
  };

  useEffect(() => {
    const dashboards = localStorage.getItem("staff_dashboard");
    if (dashboards) {
      setStaffDashboard(JSON.parse(dashboards));
    }
    LIST_SUB_TANKS_API();
  }, []);

  // Send notification
  const SEND_NOTIFICATION_MASTER = async (recordId) => {
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("voyage_id", staffDashboard?.voyage_details?.[0]?.id);
      FORM_DATA.append("record_id", recordId);
      FORM_DATA.append(
        "description",
        "A new sulphur bunker details record has been created. Please review and confirm it at your earliest convenience"
      );

      const response = await post(
        ApiURL?.sulphur_bd_requests_notification_st,
        FORM_DATA
      );
      if (response?.status) {
        SuccessToast("Congratulations!", response?.message || "Success");
      } else {
        ErrorToast("Error", response?.message || "Something went wrong.");
      }
    } catch (error) {
      ErrorToast("Request Failed", "Could not complete the request.");
    }
  };

  // Save bunker details
  const ADD_BUNKER_DETAILS = async () => {
    setLoader(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("voyage_id", staffDashboard?.voyage_details?.[0]?.id);
      FORM_DATA.append("bunker_date", form.bunkerDate);
      FORM_DATA.append("bdn_reference_no", form.bdnReferenceNo);
      FORM_DATA.append("bunkering_location", form.bunkeringLocation);
      FORM_DATA.append("supplier_name", form.supplierName);
      FORM_DATA.append("charterer_or_owner", form.chartererOrOwner);
      FORM_DATA.append("barge_name_and_imo", form.bargeNameAndImo);
      FORM_DATA.append("grade", form.grade);
      FORM_DATA.append("density", form.density);
      FORM_DATA.append("sulphur", form.sulphur);
      FORM_DATA.append("chief_eng_sign", form.chiefEngSign);
      FORM_DATA.append("totalquantity", form.totalquantity);
      FORM_DATA.append("tanks", JSON.stringify(form.tanks));

      const res = await post(ApiURL?.add_bunker_details_st_bd, FORM_DATA);
      const responseData = res?.response?.data || res;

      if (responseData?.status === true) {
        SuccessToast("Congratulation!!", responseData?.message);
        if (designation && designation !== "Chief Engineer") {
          SEND_NOTIFICATION_MASTER(responseData?.id);
        }
        navigate("/list-bunker-detail");
      } else {
        let newErrors = responseData?.errors || {};
        // If error is returned as a direct string message for a specific field
        if (responseData?.error && typeof responseData.error === "string") {
          if (responseData.error.toLowerCase().includes("totalquantity") || responseData.error.toLowerCase().includes("total quantity")) {
            newErrors = { ...newErrors, totalquantity: responseData.error };
          } else if (responseData.error.toLowerCase().includes("sulphur")) {
            newErrors = { ...newErrors, sulphur: responseData.error };
          }
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
          const errorMessage =
            responseData?.message ||
            (typeof responseData?.error === "string" ? responseData.error : "Validation Failed");
          ErrorToast("Error", errorMessage);
        }
      }
    } catch (error) {
      console.error("Error while submitting bunker details:", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <VoyageDetails staff_dashboard={staffDashboard?.voyage_details} />
      <div className="white-container">
        <h3>Bunker Record Information</h3>
        <br />

        {/* Row 1 */}
        <div className="row">
          <div className="col-6">
            <AppInput
              type="date"
              label="Bunker Date"
              value={form.bunkerDate}
              onChange={(e) => handleChange("bunkerDate", e.target.value)}
            />
          </div>
          <div className="col-6">
            <AppInput
              label="BDN Reference No."
              value={form.bdnReferenceNo}
              onChange={(e) => handleChange("bdnReferenceNo", e.target.value)}
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="row">
          <div className="col-6">
            <AppInput
              label="Bunkering Location"
              value={form.bunkeringLocation}
              onChange={(e) =>
                handleChange("bunkeringLocation", e.target.value)
              }
            />
          </div>
          <div className="col-6">
            <AppInput
              label="Name of the Supplier"
              value={form.supplierName}
              onChange={(e) => handleChange("supplierName", e.target.value)}
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="row">
          <div className="col-6">
            <AppInput
              label="Bunker Supplied by Charterer/Owner"
              value={form.chartererOrOwner}
              onChange={(e) => handleChange("chartererOrOwner", e.target.value)}
            />
          </div>
          <div className="col-6">
            <AppInput
              label="Barge Name details / IMO No."
              value={form.bargeNameAndImo}
              onChange={(e) => handleChange("bargeNameAndImo", e.target.value)}
            />
          </div>
        </div>

        {/* Row 4 */}
        <div className="row">
          <div className="col-6">
            <label className="form-label">Grade</label>
            <Select
              value={form.grade}
              style={{ width: "100%" }}
              placeholder="Select Fuel Grade"
              options={fuelTypeOptions}
              onChange={(value) => handleChange("grade", value)}
            />
          </div>
          <div className="col-6">
            <AppInput
              label="Density at 15°C (Kg/m³)"
              value={form.density}
              onChange={(e) => handleChange("density", e.target.value)}
            />
          </div>
        </div>

        {/* Row 5 */}
        <div className="row">
          <div className="col-6">
            <AppInput
              label="Sulphur (%m/m)"
              value={form.sulphur}
              onChange={(e) => handleChange("sulphur", e.target.value)}
              errors={errors.sulphur}
            />
          </div>
          <div className="col-6">
            <AppInput
              type="number"
              min={0}
              label=" Total Quantity (MT)"
              value={form.totalquantity}
              onChange={(e) => handleChange("totalquantity", e.target.value)}
              errors={errors.totalquantity}
            />
          </div>
        </div>

        {/* Row 6 */}
        <div className="row">
          <div className="col-6">
            <AppInput
              label="Chief Eng. Name & Sign"
              value={form.chiefEngSign}
              onChange={(e) => handleChange("chiefEngSign", e.target.value)}
            />
          </div>
        </div>

        {/* Tanks Section */}
        <div className="mt-3 input-box p-3">
          {form.tanks.map((tank, index) => (
            <Row gutter={16} key={index} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className="form-label">Tank No.</label>
                <Select
                  value={tank.tankId}
                  onChange={(value) => handleTankChange(index, value)}
                  placeholder="Select Tank"
                  style={{ width: "100%" }}
                >
                  {subTankList?.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {`${t.id} - ${t.category_name} (${
                        t.fuel_type || "No fuel"
                      })`}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <label className="form-label">Quantity (MT)</label>
                <Input
                  type="number"
                  min={0}
                  value={tank.quantity}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  placeholder="Enter Quantity"
                />
              </Col>
            </Row>
          ))}

          <Button type="dashed" onClick={handleAddTank}>
            + Add Another Tank
          </Button>
        </div>

        {/* Submit */}
        <div className="input-box mt-3">
          <Button loading={loader} type="primary" onClick={ADD_BUNKER_DETAILS}>
            Save
          </Button>
        </div>
      </div>
    </>
  );
}

export default AddBunkerDetails;
